import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useContext } from "react";
import { DarkModeContext } from "../../context/darkModeContext";
import "./CustomDataGrid.scss";

// ─── Hook mobile ──────────────────────────────────────────────────────────────
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [breakpoint]);
  return isMobile;
};

// ─── Card de fila ─────────────────────────────────────────────────────────────
// Renderiza cada columna como un campo de la card.
// Columnas con hideInCard=true o field="__check__" se omiten.
const RowCard = ({ row, columns, onClick }) => {
  // Separar la columna de acciones del resto
  const actionCol  = columns.find((c) => c.field === "acciones" || c.isAction);
  const dataFields = columns.filter(
    (c) => c.field !== "acciones" && !c.isAction && !c.hideInCard && c.field !== "__check__"
  );

  return (
    <div
      className={`dg-card ${onClick ? "dg-card--clickable" : ""}`}
      onClick={onClick ? () => onClick({ row }) : undefined}
    >
      <div className="dg-card__body">
        {dataFields.map((col) => {
          const value = row[col.field];
          const rendered = col.renderCell ? col.renderCell({ value, row }) : value;
          if (rendered === null || rendered === undefined || rendered === "") return null;

          // La primera columna visible actúa como título de la card
          const isFirst = col === dataFields[0];

          return (
            <div
              key={col.field}
              className={`dg-card__field ${isFirst ? "dg-card__field--title" : ""}`}
            >
              {!isFirst && (
                <span className="dg-card__label">{col.headerName}</span>
              )}
              <span className="dg-card__value">{rendered}</span>
            </div>
          );
        })}
      </div>

      {actionCol && (
        <div className="dg-card__actions">
          {actionCol.renderCell({ value: row[actionCol.field], row })}
        </div>
      )}
    </div>
  );
};

// ─── CustomDataGrid ───────────────────────────────────────────────────────────
export const CustomDataGrid = ({
  rows,
  columns,
  pageSize,
  rowCount,
  onRowClick = false,
  onPageChange,
  onPageSizeChange,
  isLoading,
  onSelectionChange = false,
  onConfirmSelection = false,
  enableCheckboxSelection = false,
  enableSelectionFinder = false,
  enableSelectionClick = true,
  enablePagination = true,
  mobileBreakpoint = 768,        // px en los que se activan las cards
  mobileCardDisabled = false,    // forzar tabla aunque sea mobile
}) => {
  const [selectionModel, setSelectionModel] = useState([]);
  const { darkMode } = useContext(DarkModeContext);
  const isMobile = useIsMobile(mobileBreakpoint);

  const showCards = isMobile && !mobileCardDisabled;

  // ── Handlers selección ────────────────────────────────────────────────────
  const handleSelectAll = () => {
    const allRows = rows.map((r) => r.id);
    setSelectionModel(allRows);
    onSelectionChange && onSelectionChange(rows);
  };

  const handleDeselectAll = () => {
    setSelectionModel([]);
    onSelectionChange && onSelectionChange([]);
  };

  const handleConfirmSelection = () => {
    const selectedRows = rows.filter((r) => selectionModel.includes(r.id));
    onConfirmSelection && onConfirmSelection(selectedRows);
  };

  const handleRowClick = (params) => {
    if (enableSelectionClick) {
      onRowClick && onRowClick(params);
    } else {
      setSelectionModel((prev) =>
        prev.includes(params.row.id)
          ? prev.filter((id) => id !== params.row.id)
          : [...prev, params.row.id]
      );
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={`datagridWrapper ${darkMode ? "dark" : ""}`}>
        <div className="dg-loading">
          <div className="dg-loading__spinner" />
          <span>Cargando datos...</span>
        </div>
      </div>
    );
  }

  // ── Vista CARDS (mobile) ─────────────────────────────────────────────────
  if (showCards) {
    return (
      <div className={`datagridWrapper dg-cards-view ${darkMode ? "dark" : ""}`}>

        {enableSelectionFinder && (
          <div className="dg-selection-toolbar">
            <button onClick={handleSelectAll}>Seleccionar Todos</button>
            <button onClick={handleDeselectAll}>Deseleccionar</button>
            <button onClick={handleConfirmSelection} disabled={selectionModel.length === 0}>
              Confirmar ({selectionModel.length})
            </button>
          </div>
        )}

        <div className="dg-cards-list">
          {rows.length === 0 ? (
            <div className="dg-empty">Sin datos</div>
          ) : (
            rows.map((row) => (
              <RowCard
                key={row.id}
                row={row}
                columns={columns}
                onClick={enableSelectionClick && onRowClick ? onRowClick : null}
              />
            ))
          )}
        </div>

        {enablePagination && (
          <div className="dg-mobile-footer">
            <span className="dg-mobile-footer__count">
              Total: <strong>{rowCount}</strong>
            </span>
          </div>
        )}
      </div>
    );
  }

  // ── Vista TABLE (desktop) ─────────────────────────────────────────────────
  return (
    <div className={`datagridWrapper ${darkMode ? "dark" : ""}`}>

      {enableSelectionFinder && (
        <div className="dg-selection-toolbar">
          <div>
            <button onClick={handleSelectAll}>Seleccionar Todos</button>
            <button onClick={handleDeselectAll} style={{ marginLeft: "10px" }}>
              Deseleccionar Todos
            </button>
            <button
              onClick={handleConfirmSelection}
              disabled={selectionModel.length === 0}
              style={{ marginLeft: "10px" }}
            >
              Confirmar Selección
            </button>
          </div>
          <div style={{ fontWeight: "bold", fontSize: "16px" }}>
            {`Ítems seleccionados: ${selectionModel.length}`}
          </div>
        </div>
      )}

      <DataGrid
        className="datagrid"
        rows={rows}
        rowHeight={28}
        columns={columns}
        checkboxSelection={enableCheckboxSelection}
        disableSelectionOnClick={false}
        selectionModel={selectionModel}
        onRowClick={enableSelectionClick ? handleRowClick : undefined}
        onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
        paginationMode={enablePagination ? "server" : undefined}
        pageSize={enablePagination ? pageSize : rows.length}
        rowCount={enablePagination ? rowCount : rows.length}
        hideFooterRowCount={true}
        onPageChange={enablePagination ? (newPage) => onPageChange(newPage + 1) : undefined}
        onPageSizeChange={enablePagination ? (newPageSize) => onPageSizeChange(newPageSize) : undefined}
        autoHeight={enablePagination}
        hideFooterPagination={!enablePagination}
        components={{ Pagination: () => null }}
        sx={{
          "& .MuiDataGrid-root":              { border: "none" },
          "& .MuiDataGrid-cell":              { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: darkMode ? "#fff" : "#000" },
          "& .MuiDataGrid-columnHeaderTitle": { color: darkMode ? "#ddd" : "#333" },
          "& .MuiDataGrid-row":               { backgroundColor: darkMode ? "#121212" : "#fff" },
          "& .MuiDataGrid-columnHeaders":     { backgroundColor: darkMode ? "#1e1e1e" : "#f5f5f5" },
          "& .MuiDataGrid-footerContainer":   { backgroundColor: darkMode ? "#1e1e1e" : "#f5f5f5", color: darkMode ? "#ccc" : "#333" },
        }}
      />
    </div>
  );
};

export default CustomDataGrid;